import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useSwipeable } from 'react-swipeable';
import {
  AppBar,
  Toolbar,
  IconButton,
  Typography,
  Box,
  Stepper,
  Step,
  StepLabel,
  Button,
  Drawer,
  List,
  ListItem,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Rating,
  Snackbar,
  Alert,
} from '@mui/material';
import {
  ArrowBack,
  Menu,
  Timer,
  ChevronLeft,
  ChevronRight,
} from '@mui/icons-material';
import { Recipe } from '../utils/MockAPI';
import { mockGetRecipeById } from '../utils/MockAPI';
import { useAuth } from '../hooks/useAuth';
import { useRecipeRating } from '../hooks/useRecipeRating';

const CookMode: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { userId } = useAuth();
  const [recipe, setRecipe] = useState<Recipe | null>(null);
  const [loading, setLoading] = useState(true);
  const [currentStep, setCurrentStep] = useState(0);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'error' as 'error' | 'success' });

  const {
    ratingOpen,
    rating,
    setRating,
    handleRatingSubmit,
    openRatingDialog,
    closeRatingDialog
  } = useRecipeRating(id || '', userId, () => {
    setSnackbar({ open: true, message: 'Thank you for helping to make Create A Dish better!', severity: 'success' });
    setTimeout(() => navigate('/results'), 2000);
  });

  useEffect(() => {
    const fetchRecipe = async () => {
      if (!id) return;
      setLoading(true);
      try {
        const data = await mockGetRecipeById(id);
        setRecipe(data);
        setSnackbar({ open: true, message: 'Recipe loaded for cooking!', severity: 'success' });
      } catch (error) {
        console.error('Error fetching recipe:', error);
        setSnackbar({ open: true, message: 'Failed to load recipe. Please try again.', severity: 'error' });
      } finally {
        setLoading(false);
      }
    };
    fetchRecipe();
  }, [id]);

  const handleNext = () => {
    if (recipe && currentStep < recipe.steps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else if (recipe && currentStep === recipe.steps.length - 1) {
      openRatingDialog();
    }
  };

  const handlePrev = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const swipeHandlers = useSwipeable({
    onSwipedLeft: handleNext,
    onSwipedRight: handlePrev,
    preventScrollOnSwipe: true,
    trackMouse: true,
  });

  const handleTimer = () => {
    // Placeholder for timer functionality
    alert('Timer started for 5 minutes');
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <Typography>Loading...</Typography>
      </Box>
    );
  }

  if (!recipe) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography>Recipe not found</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ height: '100vh', display: 'flex', flexDirection: 'column', maxWidth: '1200px', mx: 'auto' }}>
      <AppBar position="static">
        <Toolbar>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <IconButton edge="start" sx={{ color: 'white' }} onClick={() => navigate('/recipe/' + id)} aria-label="Back to recipe">
              <ArrowBack />
            </IconButton>
            <Typography variant="body2" sx={{ ml: 1, fontSize: { xs: '1rem', md: '1.25rem' }, fontWeight: 'bold', color: 'white', textShadow: '1px 1px 2px rgba(0,0,0,0.3)', cursor: 'pointer', '&:hover': { opacity: 0.8 } }} onClick={() => navigate('/recipe/' + id)}>
              Recipe
            </Typography>
          </Box>
          <Typography variant="h6" sx={{ flexGrow: 1, textAlign: 'center', fontSize: { xs: '1.25rem', md: '1.5rem' }, fontWeight: 'bold', color: 'white', textShadow: '1px 1px 2px rgba(0,0,0,0.3)' }}>
            Cooking: {recipe.title}
          </Typography>
          <IconButton sx={{ color: 'white' }} onClick={() => setDrawerOpen(true)} aria-label="Open ingredients drawer">
            <Menu />
          </IconButton>
        </Toolbar>
      </AppBar>

      <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }} {...swipeHandlers}>
        <Stepper activeStep={currentStep} sx={{ p: 2 }}>
          {recipe.steps.map((_, index) => (
            <Step key={index}>
              <StepLabel />
            </Step>
          ))}
        </Stepper>

        <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', p: 2 }}>
          <Typography variant="h4" sx={{ textAlign: 'center', mb: 4, fontSize: { xs: '2rem', md: '3rem' } }}>
            {recipe.steps[currentStep]}
          </Typography>
          <Button variant="contained" startIcon={<Timer />} onClick={handleTimer} sx={{ mb: 2, display: 'none' }} aria-label="Start timer">
            Start Timer
          </Button>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button variant="outlined" startIcon={<ChevronLeft />} onClick={handlePrev} disabled={currentStep === 0} aria-label="Previous step">
              Previous
            </Button>
            <Button variant="contained" endIcon={<ChevronRight />} onClick={handleNext} aria-label={currentStep === recipe.steps.length - 1 ? 'Finish cooking' : 'Next step'}>
              {currentStep === recipe.steps.length - 1 ? 'Finish' : 'Next'}
            </Button>
          </Box>
        </Box>
      </Box>

      <Drawer anchor="left" open={drawerOpen} onClose={() => setDrawerOpen(false)}>
        <Box sx={{ width: 250, p: 2 }}>
          <Typography variant="h6" gutterBottom>
            Ingredients
          </Typography>
          <List>
            {recipe.ingredients.map((ingredient, index) => (
              <ListItem key={index}>
                <ListItemText primary={ingredient} />
              </ListItem>
            ))}
          </List>
        </Box>
      </Drawer>

      <Dialog open={ratingOpen} onClose={closeRatingDialog}>
        <DialogTitle>Rate this Recipe</DialogTitle>
        <DialogContent>
          <Rating
            value={rating}
            onChange={(_, newValue) => setRating(newValue)}
            size="large"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={closeRatingDialog}>Cancel</Button>
          <Button onClick={handleRatingSubmit} variant="contained">
            Submit
          </Button>
        </DialogActions>
      </Dialog>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default CookMode;